import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePollDays } from 'components/overview/Overview';
import formatDate from 'utils/formatDate';
import {
  ExpandableFilter,
  Pagesection,
  useQueryParam,
} from '@ktag/styleguide/dist/react';
import { OverviewPollDay } from 'api/api.types';
import getIssueType, { ISSUE_FILTER_TYPES } from 'utils/getIssueType';
import { getInternalUrl } from 'utils/routing';

const Dates = () => {
  const { t } = useTranslation('wap');
  const { query } = usePollDays();
  const [yearsParams, setYearsParams]: [string, (year: string) => void] = useQueryParam(
    'years',
    '',
  );

  const selectedYears: string[] = yearsParams ? yearsParams.split(',') : [];

  const formatOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    year: 'numeric',
    day: undefined,
  };

  const onYearChange = (years: string[]) => {
    const selectedYearsJoined = years.join(',');
    setYearsParams(selectedYearsJoined);
  };

  // first poll is not displayed in the archive
  const allPollingDays = query?.data?.data?.slice(1);

  const distinctYears = allPollingDays
    ?.map((pollDay) => new Date(pollDay.date).getFullYear())
    .reduce((years, year) => {
      if (!years.includes(year)) {
        years.push(year);
      }
      return years;
    }, [])
    .map((year) => {
      return { label: year.toString(), value: year.toString() };
    });

  const filteredPollingDays = allPollingDays?.filter((pollingDay) => {
    if (!yearsParams.length) return true;
    return yearsParams.includes(new Date(pollingDay.date).getFullYear().toString());
  });

  const getNumberOfPolls = (pollingDay: OverviewPollDay) => {
    const electionTypes = {
      nationalPoll: {
        translationString: 'national_polls',
        count: 0,
        sort_key: 1,
      },
      cantonalPoll: {
        translationString: 'cantonal_polls',
        count: 0,
        sort_key: 2,
      },
      nationalCouncilElection: {
        translationString: 'national_council_election',
        count: 0,
        sort_key: 3,
      },
      grossratElection: {
        translationString: 'grossrat_election',
        count: 0,
        sort_key: 4,
      },
      councilOfStatesElection: {
        translationString: 'council_of_states_election',
        count: 0,
        sort_key: 5,
      },
      governmentCouncilElection: {
        translationString: 'government_council_election',
        count: 0,
        sort_key: 6,
      },
      localElection: {
        translationString: 'local_elections',
        count: 0,
        sort_key: 7,
      },
    };
    pollingDay.issues.forEach((issue) => {
      const type = getIssueType(issue);
      if (type === ISSUE_FILTER_TYPES.poll) {
        if (issue.scope === 'federal') {
          electionTypes.nationalPoll.count += 1;
        } else {
          electionTypes.cantonalPoll.count += 1;
        }
      } else if (type === ISSUE_FILTER_TYPES.nationalCouncil) {
        electionTypes.nationalCouncilElection.count += 1;
      } else if (type === ISSUE_FILTER_TYPES.grossRat) {
        electionTypes.grossratElection.count += 1;
      } else if (type === ISSUE_FILTER_TYPES.governmentCouncil) {
        if (issue.title.de.toLowerCase().includes('regierungsrat')) {
          electionTypes.governmentCouncilElection.count += 1;
        } else {
          electionTypes.councilOfStatesElection.count += 1;
        }
      } else if (type === ISSUE_FILTER_TYPES.localElection) {
        electionTypes.localElection.count += 1;
      }
    });
    const result = Object.keys(electionTypes)
      .map((type) => {
        return {
          translationString: electionTypes[type].translationString,
          count: electionTypes[type].count,
          sort_key: electionTypes[type].sort_key,
        };
      })
      .filter(({ count }) => count);
    return result.sort((a, b) => a.sort_key - b.sort_key);
  };

  return (
    <div className="wap__dates">
      <Pagesection>
        <ExpandableFilter
          label={t('dates_filter')}
          name={t('dates_filter')}
          type="multiselect"
          options={distinctYears}
          asChips
          numberOfResults={0}
          onChange={onYearChange}
          initialValue={selectedYears}
        />
      </Pagesection>
      <Pagesection type="medium">
        <ul className="wap__archive-dates">
          {filteredPollingDays?.map((pollingDay) => (
            <li key={pollingDay.id} className="dateteaser">
              <Link to={getInternalUrl(pollingDay, 'poll_day')}>
                <div className="dateteaser__inner">
                  <h2 className="dateteaser__title">
                    {formatDate(pollingDay.date, formatOptions)}
                  </h2>
                  <ul className="dateteaser__meta">
                    {getNumberOfPolls(pollingDay).map((type) => (
                      <li key={type.translationString}>
                        <span>{t(type.translationString, { count: type.count })}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </Pagesection>
    </div>
  );
};

export default Dates;
